import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import {
  isNewVersionDeployed,
  reloadToLatestVersion,
  versionCheckAvailable,
} from '../utils/appVersion';

// Offers a reload when a newer build goes live while this tab is open.
//
// Deliberately an OFFER, not an automatic reload. Someone may be halfway through writing a
// prescription, and throwing the page away under them to save a click is a bad trade. Login is the
// one place a reload is forced, because nothing is in progress there (see Login.js).
//
// Checked on an interval and again whenever the tab is brought back to the front, which is the
// moment someone returns to a page they left open before the deploy.

const CHECK_EVERY_MS = 5 * 60 * 1000;

const NewVersionBanner = () => {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    if (await isNewVersionDeployed()) setAvailable(true);
  }, []);

  useEffect(() => {
    if (!versionCheckAvailable()) return undefined;
    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [check]);

  if (!available || dismissed) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[60] max-w-sm bg-white rounded-2xl shadow-2xl border border-emerald-200 p-4 print:hidden">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-4.5 h-4.5 text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-sm">A newer version is available</p>
          <p className="text-xs text-gray-500 mt-0.5">
            This tab is running an older version of the app. Reload when you reach a good stopping
            point; anything unsaved on screen will be lost.
          </p>
          <button
            onClick={reloadToLatestVersion}
            className="mt-2.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Reload now
          </button>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded-lg hover:bg-gray-100 flex-shrink-0"
          title="Dismiss"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default NewVersionBanner;
